/*
  Velune · base "mm": lista de desejos, quantidade e cores do cartão, e o carrossel <velune-mm-carousel>.
  Carregado em todas as páginas por snippets/velune-global.liquid.

  window.VeluneWishlist (lista de desejos guardada no navegador, sem conta)
    .handles / .items / .list()   cópia da lista de handles
    .count                         quantos produtos
    .has(handle) .add(handle) .remove(handle) .toggle(handle) .clear()
    Evento "velune:wishlist-change" no document (sobe até a window), com
    detail = { handles, count, handle, added }. Também é disparado uma vez na carga da página,
    para quem mostra a contagem (cabeçalho) não depender da ordem dos scripts.
*/

/* ------------------------------------------------------------------ */
/* Lista de desejos                                                   */
/* ------------------------------------------------------------------ */

const WISHLIST_KEY = 'velune:wishlist';
const WISHLIST_EVENT = 'velune:wishlist-change';

/** @returns {string[]} */
function readWishlist() {
  try {
    const stored = JSON.parse(window.localStorage.getItem(WISHLIST_KEY) || '[]');
    if (!Array.isArray(stored)) return [];
    return [...new Set(stored.filter((item) => typeof item === 'string' && item !== ''))];
  } catch {
    return [];
  }
}

/** Cópia em memória: o coração continua funcionando na página mesmo sem acesso ao localStorage. */
let wishlistHandles = readWishlist();

function writeWishlist() {
  try {
    window.localStorage.setItem(WISHLIST_KEY, JSON.stringify(wishlistHandles));
  } catch {
    /* navegação privada ou armazenamento cheio: vale só para esta página */
  }
}

/**
 * @param {string | null} [handle]
 * @param {boolean | null} [added]
 */
function emitWishlistChange(handle = null, added = null) {
  syncWishlistButtons();
  document.dispatchEvent(
    new CustomEvent(WISHLIST_EVENT, {
      bubbles: true,
      detail: { handles: [...wishlistHandles], count: wishlistHandles.length, handle, added },
    })
  );
}

const VeluneWishlist = {
  eventName: WISHLIST_EVENT,
  storageKey: WISHLIST_KEY,

  get handles() {
    return [...wishlistHandles];
  },

  get items() {
    return [...wishlistHandles];
  },

  get count() {
    return wishlistHandles.length;
  },

  list() {
    return [...wishlistHandles];
  },

  /** @param {string} handle */
  has(handle) {
    return wishlistHandles.includes(handle);
  },

  /** @param {string} handle */
  add(handle) {
    if (!handle || wishlistHandles.includes(handle)) return false;
    wishlistHandles = [...wishlistHandles, handle];
    writeWishlist();
    emitWishlistChange(handle, true);
    return true;
  },

  /** @param {string} handle */
  remove(handle) {
    if (!wishlistHandles.includes(handle)) return false;
    wishlistHandles = wishlistHandles.filter((item) => item !== handle);
    writeWishlist();
    emitWishlistChange(handle, false);
    return true;
  },

  /**
   * @param {string} handle
   * @returns {boolean} true quando o produto ficou na lista
   */
  toggle(handle) {
    if (!handle) return false;
    if (wishlistHandles.includes(handle)) {
      this.remove(handle);
      return false;
    }
    this.add(handle);
    return true;
  },

  clear() {
    if (wishlistHandles.length === 0) return;
    wishlistHandles = [];
    writeWishlist();
    emitWishlistChange(null, false);
  },
};

// @ts-ignore
window.VeluneWishlist = VeluneWishlist;

/** Marca e desmarca os corações que estão na página. */
function syncWishlistButtons() {
  for (const button of document.querySelectorAll('[data-mm-wish]')) {
    if (!(button instanceof HTMLElement)) continue;
    const active = wishlistHandles.includes(button.dataset.handle || '');
    if (button.getAttribute('aria-pressed') !== String(active)) {
      button.setAttribute('aria-pressed', String(active));
    }
    const label = active ? button.dataset.labelRemove : button.dataset.labelAdd;
    if (label && button.getAttribute('aria-label') !== label) button.setAttribute('aria-label', label);
  }
}

// Outra aba mudou a lista
window.addEventListener('storage', (event) => {
  if (event.key !== WISHLIST_KEY) return;
  wishlistHandles = readWishlist();
  emitWishlistChange(null, null);
});

/* ------------------------------------------------------------------ */
/* Cartão: coração, quantidade, cores e botão de adicionar            */
/* ------------------------------------------------------------------ */

const QTY_MAX = 99;

/** @param {HTMLInputElement} input */
function clampQuantity(input) {
  const min = Number.parseInt(input.min, 10) || 1;
  const max = Number.parseInt(input.max, 10) || QTY_MAX;
  const value = Number.parseInt(input.value, 10);
  const clamped = Math.min(max, Math.max(min, Number.isFinite(value) ? value : min));
  if (String(clamped) !== input.value) input.value = String(clamped);

  const wrapper = input.closest('[data-mm-qty]');
  const minus = wrapper?.querySelector('[data-mm-qty-step="-1"]');
  const plus = wrapper?.querySelector('[data-mm-qty-step="1"]');
  if (minus instanceof HTMLButtonElement) minus.disabled = clamped <= min;
  if (plus instanceof HTMLButtonElement) plus.disabled = clamped >= max;
  return clamped;
}

/** Quantidade pedida no cartão cujo botão abriu a janela de escolha. */
let pendingQuantity = 0;

document.addEventListener(
  'click',
  (event) => {
    const target = event.target instanceof Element ? event.target : null;
    if (!target) return;

    const wish = target.closest('[data-mm-wish]');
    if (wish instanceof HTMLElement) {
      event.preventDefault();
      VeluneWishlist.toggle(wish.dataset.handle || '');
      return;
    }

    const stepButton = target.closest('[data-mm-qty-step]');
    if (stepButton instanceof HTMLElement) {
      event.preventDefault();
      const input = stepButton.closest('[data-mm-qty]')?.querySelector('input');
      if (input instanceof HTMLInputElement) {
        const step = Number.parseInt(stepButton.dataset.mmQtyStep || '0', 10) || 0;
        input.value = String((Number.parseInt(input.value, 10) || 1) + step);
        clampQuantity(input);
      }
      return;
    }

    const swatchArrow = target.closest('[data-mm-sw-prev], [data-mm-sw-next]');
    if (swatchArrow instanceof HTMLElement) {
      event.preventDefault();
      const track = swatchArrow.closest('[data-mm-swatches]')?.querySelector('[data-mm-sw-track]');
      if (track instanceof HTMLElement) {
        const direction = swatchArrow.hasAttribute('data-mm-sw-next') ? 1 : -1;
        track.scrollBy({ left: direction * Math.max(52, track.clientWidth - 26) });
      }
      return;
    }

    const choose = target.closest('[data-mm-choose]');
    if (choose instanceof HTMLElement) {
      const input = choose.closest('.mm-card')?.querySelector('.mm-qty__input');
      pendingQuantity =
        input instanceof HTMLInputElement && input.offsetParent !== null ? clampQuantity(input) : 0;

      // Sem a compra rápida do tema (desligada nas configurações), o botão leva à página do produto
      const quickAddReady = customElements.get('quick-add-component') && document.getElementById('quick-add-dialog');
      if (!quickAddReady && choose.dataset.url) {
        event.preventDefault();
        window.location.href = choose.dataset.url;
      }
    }
  },
  true
);

document.addEventListener('change', (event) => {
  if (event.target instanceof HTMLInputElement && event.target.matches('.mm-qty__input')) {
    clampQuantity(event.target);
  }
});

/** Leva a quantidade escolhida no cartão para a janela de escolha do tema, quando ela abre. */
function watchQuickAddDialog() {
  const dialog = document.getElementById('quick-add-dialog');
  if (!dialog || dialog.hasAttribute('data-mm-watched')) return;
  dialog.setAttribute('data-mm-watched', '');

  dialog.addEventListener('dialog:open', () => {
    const quantity = pendingQuantity;
    pendingQuantity = 0;
    if (quantity < 2) return;

    const selector = dialog.querySelector('quantity-selector-component');
    const input = selector?.querySelector('input[name="quantity"]');
    if (!(input instanceof HTMLInputElement)) return;

    const max = Number.parseInt(input.max, 10);
    input.value = String(Number.isFinite(max) && max > 0 ? Math.min(max, quantity) : quantity);
    input.dispatchEvent(new Event('change', { bubbles: true }));
  });
}

/** Mostra as setas da faixa de cores só quando há mais cores do que cabem. */
function syncSwatchStrip(strip) {
  const track = strip.querySelector('[data-mm-sw-track]');
  const prev = strip.querySelector('[data-mm-sw-prev]');
  const next = strip.querySelector('[data-mm-sw-next]');
  if (!(track instanceof HTMLElement) || !(prev instanceof HTMLElement) || !(next instanceof HTMLElement)) return;

  // As setas ocupam 60 px (2 × 24 + 2 vãos de 6). Sem elas, a faixa mede a largura inteira.
  const arrowsSpace = prev.hidden ? 0 : 60;
  const overflows = track.scrollWidth > track.clientWidth + arrowsSpace + 1;
  if (prev.hidden === overflows) {
    prev.hidden = !overflows;
    next.hidden = !overflows;
  }
}

/** @type {ResizeObserver | null} */
const swatchObserver =
  'ResizeObserver' in window
    ? new ResizeObserver((entries) => {
        for (const entry of entries) syncSwatchStrip(entry.target);
      })
    : null;

function watchSwatchStrips() {
  for (const strip of document.querySelectorAll('[data-mm-swatches]:not([data-mm-watched])')) {
    strip.setAttribute('data-mm-watched', '');
    syncSwatchStrip(strip);
    swatchObserver?.observe(strip);
  }
}

/** Cartões novos (rolagem infinita, recomendações, editor de temas) recebem o mesmo tratamento. */
let scanQueued = false;

function scanPage() {
  scanQueued = false;
  syncWishlistButtons();
  watchSwatchStrips();
  watchQuickAddDialog();
}

function queueScan() {
  if (scanQueued) return;
  scanQueued = true;
  requestAnimationFrame(scanPage);
}

new MutationObserver((mutations) => {
  for (const mutation of mutations) {
    for (const node of mutation.addedNodes) {
      if (!(node instanceof Element)) continue;
      if (node.matches('.mm-card, [data-mm-wish]') || node.querySelector('.mm-card, [data-mm-wish]')) {
        queueScan();
        return;
      }
    }
  }
}).observe(document.documentElement, { childList: true, subtree: true });

let firstRunDone = false;

function firstRun() {
  if (firstRunDone) return;
  firstRunDone = true;
  scanPage();
  emitWishlistChange(null, null);
}

if (document.readyState === 'complete') {
  firstRun();
} else {
  // Scripts de módulo rodam antes do DOMContentLoaded: quem escuta o evento já está pronto quando ele sai.
  document.addEventListener('DOMContentLoaded', firstRun, { once: true });
  window.addEventListener('load', firstRun, { once: true });
  scanPage();
}

/* ------------------------------------------------------------------ */
/* Carrossel                                                          */
/* ------------------------------------------------------------------ */

const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');

class VeluneMmCarousel extends HTMLElement {
  /** @type {HTMLElement | null} */
  #track = null;
  /** @type {HTMLButtonElement | null} */
  #prev = null;
  /** @type {HTMLButtonElement | null} */
  #next = null;
  /** @type {HTMLElement | null} */
  #dots = null;
  /** @type {HTMLElement | null} */
  #progress = null;
  /** @type {ResizeObserver | null} */
  #resizeObserver = null;
  /** @type {MutationObserver | null} */
  #mutationObserver = null;
  /** @type {AbortController | null} */
  #abort = null;
  #frame = 0;
  /** Posição de rolagem de cada slide. @type {number[]} */
  #positions = [];
  /** Índice do primeiro slide de cada página. @type {number[]} */
  #pageStarts = [0];
  #maxIndex = 0;
  #dotsSignature = '';

  connectedCallback() {
    this.#track = this.querySelector('[data-mm-track]');
    if (!this.#track) return;

    const prev = this.querySelector('[data-mm-prev]');
    const next = this.querySelector('[data-mm-next]');
    this.#prev = prev instanceof HTMLButtonElement ? prev : null;
    this.#next = next instanceof HTMLButtonElement ? next : null;
    this.#dots = this.querySelector('[data-mm-dots]');
    this.#progress = this.querySelector('[data-mm-progress]');

    this.#abort?.abort();
    this.#abort = new AbortController();
    const { signal } = this.#abort;

    this.#syncPerView();

    if (!this.hasAttribute('role')) {
      this.setAttribute('role', 'group');
      this.setAttribute('aria-roledescription', 'carousel');
    }
    this.#progress?.setAttribute('aria-hidden', 'true');

    this.#prev?.addEventListener('click', () => this.previous(), { signal });
    this.#next?.addEventListener('click', () => this.next(), { signal });
    this.#track.addEventListener('scroll', this.#onScroll, { passive: true, signal });
    this.#dots?.addEventListener(
      'click',
      (event) => {
        const dot = event.target instanceof Element ? event.target.closest('[data-mm-page]') : null;
        if (dot instanceof HTMLElement) this.goToPage(Number(dot.dataset.mmPage) || 0);
      },
      { signal }
    );

    if ('ResizeObserver' in window) {
      this.#resizeObserver = new ResizeObserver(() => this.refresh());
      this.#resizeObserver.observe(this.#track);
    } else {
      window.addEventListener('resize', () => this.refresh(), { signal });
    }

    this.#mutationObserver = new MutationObserver(() => this.refresh());
    this.#mutationObserver.observe(this.#track, { childList: true });

    this.refresh();
    this.setAttribute('data-mm-ready', '');
  }

  disconnectedCallback() {
    this.#abort?.abort();
    this.#abort = null;
    this.#resizeObserver?.disconnect();
    this.#mutationObserver?.disconnect();
    cancelAnimationFrame(this.#frame);
    this.#dotsSignature = '';
  }

  /** @returns {HTMLElement[]} */
  get slides() {
    if (!this.#track) return [];
    return /** @type {HTMLElement[]} */ (
      Array.from(this.#track.children).filter((child) => child instanceof HTMLElement && !child.hidden)
    );
  }

  /** Colunas à vista, lidas do CSS (a largura dos slides e o script usam o mesmo valor). */
  get perView() {
    const value = Number.parseFloat(getComputedStyle(this).getPropertyValue('--mm-pv'));
    return value > 0 ? value : 1;
  }

  /** Linhas por página, lidas do CSS: 2 quando a vitrine mostra "2 em cima e 2 embaixo" (só abaixo de 600 px). */
  get rows() {
    const value = Number.parseInt(getComputedStyle(this).getPropertyValue('--mm-rows'), 10);
    return value > 1 ? value : 1;
  }

  /** data-loop="true": depois do último volta o primeiro, e as setas não desligam. */
  get loop() {
    return this.dataset.loop === 'true';
  }

  /** "page": a seta passa uma página; "1": passa um cartão. */
  get mode() {
    const step = this.dataset.step;
    if (step === 'page' || step === '1') return step;
    const hasProgress = this.dataset.progress === 'true' || this.classList.contains('mm-carousel--progress');
    return hasProgress && this.dataset.dots !== 'true' ? '1' : 'page';
  }

  get index() {
    const left = this.#track?.scrollLeft ?? 0;
    let closest = 0;
    let distance = Infinity;
    this.#positions.forEach((position, index) => {
      const delta = Math.abs(position - left);
      if (delta < distance - 0.5) {
        distance = delta;
        closest = index;
      }
    });
    return Math.min(closest, this.#maxIndex);
  }

  get page() {
    const index = this.index;
    if (index >= this.#maxIndex) return this.#pageStarts.length - 1;
    let page = 0;
    this.#pageStarts.forEach((start, position) => {
      if (start <= index) page = position;
    });
    return page;
  }

  next() {
    this.#move(1);
  }

  previous() {
    this.#move(-1);
  }

  /** @param {number} index */
  goTo(index) {
    if (!this.#track) return;
    const target = Math.max(0, Math.min(this.#maxIndex, Math.round(index)));
    this.#track.scrollTo({ left: this.#positions[target] ?? 0, behavior: reducedMotion.matches ? 'auto' : 'smooth' });
  }

  /** @param {number} page */
  goToPage(page) {
    const target = Math.max(0, Math.min(this.#pageStarts.length - 1, Math.round(page)));
    this.goTo(this.#pageStarts[target] ?? 0);
  }

  /** @param {1 | -1} direction */
  #move(direction) {
    const index = this.index;

    if (this.loop) {
      if (direction > 0 && index >= this.#maxIndex) {
        this.goTo(0);
        return;
      }
      if (direction < 0 && index <= 0) {
        this.goTo(this.#maxIndex);
        return;
      }
    }

    if (this.mode === '1') {
      this.goTo(index + direction);
      return;
    }

    if (direction > 0) {
      const nextStart = this.#pageStarts.find((start) => start > index);
      this.goTo(nextStart ?? this.#maxIndex);
    } else {
      const previousStarts = this.#pageStarts.filter((start) => start < index);
      this.goTo(previousStarts.length ? previousStarts[previousStarts.length - 1] ?? 0 : 0);
    }
  }

  /** Colunas pedidas nos atributos viram variáveis (cobre valores que o CSS não lista). */
  #syncPerView() {
    const desktop = Number.parseFloat(this.dataset.perView || '');
    const tablet = Number.parseFloat(this.dataset.perViewTablet || '');
    const small = Number.parseFloat(this.dataset.perViewSmall || '');
    const mobile = Number.parseFloat(this.dataset.perViewMobile || '');

    if (desktop > 0) this.style.setProperty('--mm-pv-d', String(desktop));
    if (tablet > 0) this.style.setProperty('--mm-pv-t', String(tablet));
    else if (desktop > 0) this.style.setProperty('--mm-pv-t', String(Math.min(desktop, 3)));

    const phone = mobile > 0 ? mobile : desktop > 0 ? Math.min(desktop, 2) : 0;
    if (phone > 0) this.style.setProperty('--mm-pv-m', String(phone));

    // De 600 a 768 px (data-per-view-small). Sem o atributo, a mesma regra do CSS: o carrossel de menos de 2
    // por vez (a capa) continua como no celular; os outros mostram pelo menos 3, como a grade de coleção.
    if (small > 0) this.style.setProperty('--mm-pv-s', String(small));
    else if (phone > 0) this.style.setProperty('--mm-pv-s', String(phone < 2 ? phone : Math.max(phone, 3)));
  }

  /** Mede de novo: posições, páginas, pontos, setas e barra. */
  refresh() {
    const track = this.#track;
    if (!track) return;

    // O encaixe respeita a folga real do trilho (quem zera o padding, como a capa, zera o encaixe junto)
    const padding = getComputedStyle(track).paddingLeft;
    if (track.style.scrollPaddingInline !== padding) track.style.scrollPaddingInline = padding;

    const slides = this.slides;
    const maxScroll = Math.max(0, track.scrollWidth - track.clientWidth);
    const base = slides[0]?.offsetLeft ?? 0;

    this.#positions = slides.map((slide) => Math.min(slide.offsetLeft - base, maxScroll));

    const endIndex = this.#positions.findIndex((position) => position >= maxScroll - 1);
    this.#maxIndex = maxScroll <= 1 ? 0 : endIndex === -1 ? slides.length - 1 : endIndex;

    const visible = Math.max(1, Math.floor(this.perView + 0.01)) * this.rows;
    const starts = [];
    for (let start = 0; start < this.#maxIndex; start += visible) starts.push(start);
    starts.push(this.#maxIndex);
    this.#pageStarts = starts;

    const mode = this.mode;
    if (this.dataset.mmMode !== mode) this.dataset.mmMode = mode;
    slides.forEach((slide, index) => slide.classList.toggle('is-snap', mode === '1' || starts.includes(index)));

    this.toggleAttribute('data-mm-static', this.#maxIndex === 0);
    this.#renderDots();
    this.#update();
  }

  #renderDots() {
    if (!this.#dots) return;

    const count = this.#pageStarts.length;
    const signature = String(count);
    if (signature === this.#dotsSignature && this.#dots.childElementCount === count) return;
    this.#dotsSignature = signature;

    const fragment = document.createDocumentFragment();
    for (let page = 0; page < count; page += 1) {
      const dot = document.createElement('button');
      dot.type = 'button';
      dot.className = 'mm-carousel__dot';
      dot.dataset.mmPage = String(page);
      dot.setAttribute('aria-label', `Go to page ${page + 1} of ${count}`);
      fragment.append(dot);
    }
    this.#dots.replaceChildren(fragment);
  }

  #onScroll = () => {
    cancelAnimationFrame(this.#frame);
    this.#frame = requestAnimationFrame(() => this.#update());
  };

  #update() {
    const track = this.#track;
    if (!track) return;

    const maxScroll = Math.max(0, track.scrollWidth - track.clientWidth);
    const left = Math.max(0, Math.min(maxScroll, track.scrollLeft));

    const loop = this.loop;
    if (this.#prev) this.#prev.disabled = !loop && left <= 1;
    if (this.#next) this.#next.disabled = !loop && left >= maxScroll - 1;

    if (this.#dots) {
      const page = this.page;
      Array.from(this.#dots.children).forEach((dot, index) => {
        if (index === page) dot.setAttribute('aria-current', 'true');
        else dot.removeAttribute('aria-current');
      });
    }

    if (this.#progress) {
      // Um passo por cartão que ainda entra: começa em 1/passos e chega a 100% no fim, como na referência
      const steps = this.#maxIndex + 1;
      const ratio = maxScroll > 0 ? left / maxScroll : 1;
      const fill = (1 + ratio * (steps - 1)) / steps;
      this.#progress.style.setProperty('--mm-progress', `${(fill * 100).toFixed(2)}%`);
    }
  }
}

if (!customElements.get('velune-mm-carousel')) {
  customElements.define('velune-mm-carousel', VeluneMmCarousel);
}
